import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, Clock, DollarSign, CheckCircle, AlertCircle, FileText, Wrench, CreditCard, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../utils/api';
import { StatusBadge, LoadingSpinner } from '../common/Badges';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try { const data = await api('/api/admin/stats'); setStats(data); }
    catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  };

  if (loading) return <LoadingSpinner />;

  const totalRevenue = (stats?.revenue?.total_paid || 0) + (stats?.revenue?.total_job_revenue || 0);

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-red-500 font-bold tracking-[0.3em] mb-1">COMMAND CENTER</p>
          <h1 className="font-heading text-3xl font-bold text-white">Dashboard</h1>
        </div>
        <button onClick={() => navigate('/admin/settings')} className="btn-secondary text-sm flex items-center gap-2" data-testid="admin-settings-btn">
          <Settings className="w-4 h-4" />Settings
        </button>
      </div>

      {/* Top KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div onClick={() => navigate('/admin/requests')} className="relative overflow-hidden rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700/50 p-5 cursor-pointer hover:border-red-500/40 transition-all group" data-testid="stat-total-requests">
          <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/5 rounded-full -translate-y-6 translate-x-6"></div>
          <FileText className="w-5 h-5 text-blue-400 mb-3" />
          <p className="text-3xl font-bold text-white tracking-tight">{stats?.requests?.total || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Total Requests</p>
        </div>
        <div onClick={() => navigate('/admin/requests')} className="relative overflow-hidden rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700/50 p-5 cursor-pointer hover:border-yellow-500/40 transition-all group" data-testid="stat-pending">
          <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-500/5 rounded-full -translate-y-6 translate-x-6"></div>
          <AlertCircle className="w-5 h-5 text-yellow-400 mb-3" />
          <p className="text-3xl font-bold text-white tracking-tight">{stats?.requests?.pending || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Pending</p>
          {(stats?.requests?.pending || 0) > 0 && <div className="absolute top-3 right-3 w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>}
        </div>
        <div onClick={() => navigate('/admin/tracking')} className="relative overflow-hidden rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700/50 p-5 cursor-pointer hover:border-green-500/40 transition-all group" data-testid="stat-active-today">
          <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/5 rounded-full -translate-y-6 translate-x-6"></div>
          <Clock className="w-5 h-5 text-green-400 mb-3" />
          <p className="text-3xl font-bold text-white tracking-tight">{stats?.active_today || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Active Today</p>
          {(stats?.active_today || 0) > 0 && <div className="absolute top-3 right-3 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>}
        </div>
        <div onClick={() => navigate('/admin/invoices')} className="relative overflow-hidden rounded-lg bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700/50 p-5 cursor-pointer hover:border-emerald-500/40 transition-all group" data-testid="stat-revenue">
          <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-500/5 rounded-full -translate-y-6 translate-x-6"></div>
          <DollarSign className="w-5 h-5 text-emerald-400 mb-3" />
          <p className="text-3xl font-bold text-white tracking-tight">${totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
          <p className="text-xs text-gray-500 mt-1">Total Revenue</p>
        </div>
      </div>

      {/* Middle Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Operations */}
        <div className="rounded-lg bg-gray-900 border border-gray-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
            <h2 className="text-sm font-bold text-white tracking-wide">Operations</h2>
            <span className="text-[10px] text-gray-600 tracking-widest">LIVE</span>
          </div>
          <div className="divide-y divide-gray-800/50">
            <div onClick={() => navigate('/admin/requests')} className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-gray-800/50 transition-colors" data-testid="ops-in-progress">
              <div className="flex items-center gap-3"><div className="w-2 h-2 bg-purple-500 rounded-full"></div><span className="text-sm text-gray-300">In Progress</span></div>
              <span className="text-sm font-bold text-white">{stats?.requests?.in_progress || 0}</span>
            </div>
            <div onClick={() => navigate('/admin/requests')} className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-gray-800/50 transition-colors" data-testid="ops-assigned">
              <div className="flex items-center gap-3"><div className="w-2 h-2 bg-blue-500 rounded-full"></div><span className="text-sm text-gray-300">Assigned</span></div>
              <span className="text-sm font-bold text-white">{stats?.requests?.assigned || 0}</span>
            </div>
            <div onClick={() => navigate('/admin/requests')} className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-gray-800/50 transition-colors" data-testid="ops-completed">
              <div className="flex items-center gap-3"><div className="w-2 h-2 bg-green-500 rounded-full"></div><span className="text-sm text-gray-300">Completed</span></div>
              <span className="text-sm font-bold text-white">{stats?.requests?.completed || 0}</span>
            </div>
            <div onClick={() => navigate('/admin/technicians')} className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-gray-800/50 transition-colors" data-testid="ops-technicians">
              <div className="flex items-center gap-3"><Wrench className="w-3.5 h-3.5 text-red-500" /><span className="text-sm text-gray-300">Technicians</span></div>
              <span className="text-sm font-bold text-white">{stats?.users?.technicians || 0}</span>
            </div>
            <div onClick={() => navigate('/admin/customers')} className="flex items-center justify-between px-5 py-3.5 cursor-pointer hover:bg-gray-800/50 transition-colors" data-testid="ops-customers">
              <div className="flex items-center gap-3"><User className="w-3.5 h-3.5 text-gray-500" /><span className="text-sm text-gray-300">Customers</span></div>
              <span className="text-sm font-bold text-white">{stats?.users?.customers || 0}</span>
            </div>
          </div>
        </div>

        {/* Financial */}
        <div className="rounded-lg bg-gray-900 border border-gray-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
            <h2 className="text-sm font-bold text-white tracking-wide">Financial</h2>
            <button onClick={() => navigate('/admin/invoices')} className="text-[10px] text-red-500 hover:text-red-400 tracking-widest">VIEW ALL</button>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded bg-green-500/10 flex items-center justify-center"><CheckCircle className="w-4 h-4 text-green-400" /></div>
                <div><p className="text-xs text-gray-500">Paid Invoices</p><p className="text-sm font-bold text-white">{stats?.revenue?.paid_count || 0}</p></div>
              </div>
              <p className="text-sm font-bold text-green-400">${(stats?.revenue?.total_paid || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded bg-red-500/10 flex items-center justify-center"><AlertCircle className="w-4 h-4 text-red-400" /></div>
                <div><p className="text-xs text-gray-500">Unpaid</p><p className="text-sm font-bold text-white">{stats?.revenue?.unpaid_count || 0}</p></div>
              </div>
              <p className="text-sm font-bold text-red-400">${(stats?.revenue?.total_unpaid || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
            </div>
            <div className="pt-3 border-t border-gray-800 flex items-center justify-between">
              <p className="text-xs text-gray-500">Job Revenue</p>
              <p className="text-sm font-bold text-white">${(stats?.revenue?.total_job_revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="rounded-lg bg-gray-900 border border-gray-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-800">
            <h2 className="text-sm font-bold text-white tracking-wide">Quick Actions</h2>
          </div>
          <div className="p-3 grid grid-cols-2 gap-2">
            {[
              { label: 'Requests', icon: FileText, path: '/admin/requests', color: 'text-blue-400' },
              { label: 'Time Mgmt', icon: Clock, path: '/admin/tracking', color: 'text-green-400' },
              { label: 'Payroll', icon: DollarSign, path: '/admin/payroll', color: 'text-emerald-400' },
              { label: 'Technicians', icon: Wrench, path: '/admin/technicians', color: 'text-red-400' },
              { label: 'Gallery', icon: Camera, path: '/admin/gallery', color: 'text-purple-400' },
              { label: 'Site Content', icon: Settings, path: '/admin/site-content', color: 'text-gray-400' },
              { label: 'Invoices', icon: CreditCard, path: '/admin/invoices', color: 'text-yellow-400' },
              { label: 'Floor Plan', icon: FileText, path: '/admin/floor-plan', color: 'text-cyan-400' },
            ].map((item) => (
              <button key={item.label} onClick={() => navigate(item.path)}
                className="flex items-center gap-2.5 p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800 border border-transparent hover:border-gray-700 transition-all text-left"
                data-testid={`quick-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                <item.icon className={`w-4 h-4 ${item.color}`} />
                <span className="text-xs text-gray-300 font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Requests */}
      <div className="rounded-lg bg-gray-900 border border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-sm font-bold text-white tracking-wide">Recent Requests</h2>
          <button onClick={() => navigate('/admin/requests')} className="text-[10px] text-red-500 hover:text-red-400 tracking-widest">VIEW ALL</button>
        </div>
        {(stats?.recent_requests || []).length === 0 ? (
          <div className="p-10 text-center">
            <FileText className="w-8 h-8 mx-auto mb-2 text-gray-700" />
            <p className="text-sm text-gray-600">No requests yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-800/50">
            {(stats?.recent_requests || []).map((req) => (
              <div key={req.id} className="px-5 py-3.5 hover:bg-gray-800/30 transition-colors flex items-center justify-between" data-testid={`recent-req-${req.id}`}>
                <div className="flex items-center gap-4">
                  <p className="font-mono text-xs text-gray-600 w-20">{req.ticket_number}</p>
                  <div>
                    <p className="text-sm text-white font-medium">{req.customer_name}</p>
                    <p className="text-xs text-gray-500 capitalize">{req.service_type.replace('_', ' ')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {req.price && <p className="text-sm font-bold text-green-400">${req.price.toFixed(2)}</p>}
                  <StatusBadge status={req.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


export default AdminDashboard;
